import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import HeroCarousel from '../components/HeroCarousel';
import CreateEventCTA from '../components/CreateEventCTA';
import EventsSection from '../components/EventsSection';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';
import { fetchAllEvents, getPopularEvents, getRecommendedEvents, getUpcomingEvents } from '../services/eventService';
import { format } from 'date-fns';
import { PLACEHOLDER_EVENT_IMAGE, DEFAULT_TIMEZONE, HERO_BANNER_IMAGES } from '../config';

type Event = {
    id: number;
    title: string;
    image: string;
    venue: string;
    date: string;
    time: string;
    price: number;
    isFreeOfCharge: boolean;
};

const HomePage: React.FC = () => {
    const [nearbyEvents, setNearbyEvents] = useState<Event[]>([]);
    const [popularEvents, setPopularEvents] = useState<Event[]>([]);
    const [recommendedEvents, setRecommendedEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isSignupOpen, setIsSignupOpen] = useState(false);
    const location = useLocation();

    const handleSwitchToSignup = () => {
        setIsLoginOpen(false);
        setIsSignupOpen(true);
    };

    const handleSwitchToLogin = () => {
        setIsSignupOpen(false);
        setIsLoginOpen(true);
    };


    // Check if user was redirected from a protected route and should show login modal
    useEffect(() => {
        if (location.state?.showLoginModal) {
            // Immediately scroll to top to prevent any scroll jumping
            window.scrollTo(0, 0);
            // Small delay to ensure scroll completes before opening modal
            setTimeout(() => {
                setIsLoginOpen(true);
                // Clear the location state to prevent any issues
                window.history.replaceState({}, document.title);
            }, 100);
        }
    }, [location.state]);

    useEffect(() => {
        const loadEvents = async () => {
            try {
                // Fetch upcoming events
                const upcomingData = await getUpcomingEvents();
                const mappedUpcomingEvents = upcomingData.map((event) => ({
                    id: event.id,
                    title: event.title,
                    image: event.imageUrl || PLACEHOLDER_EVENT_IMAGE,
                    venue: event.venue,
                    date: format(new Date(event.startTime), 'do MMM, yyyy'),
                    time: new Date(event.startTime).toLocaleTimeString('en-US', { 
                        timeZone: DEFAULT_TIMEZONE,
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    }).replace(/am/g, 'AM').replace(/pm/g, 'PM'),
                    price: event.sellingPrice,
                    isFreeOfCharge: event.isFreeOfCharge,
                }));
                setNearbyEvents(mappedUpcomingEvents);

                // Fetch popular events
                const popularData = await getPopularEvents();
                const mappedPopularEvents = popularData.map((event) => ({
                    id: event.id,
                    title: event.title,
                    image: event.imageUrl || PLACEHOLDER_EVENT_IMAGE,
                    venue: event.venue,
                    date: format(new Date(event.startTime), 'do MMM, yyyy'),
                    time: new Date(event.startTime).toLocaleTimeString('en-US', { 
                        timeZone: DEFAULT_TIMEZONE,
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    }).replace(/am/g, 'AM').replace(/pm/g, 'PM'),
                    price: event.sellingPrice,
                    isFreeOfCharge: event.isFreeOfCharge,
                }));
                setPopularEvents(mappedPopularEvents);

                // Fetch recommended events
                const recommendedData = await getRecommendedEvents();
                const mappedRecommendedEvents = recommendedData.map((event) => ({
                    id: event.id,
                    title: event.title,
                    image: event.imageUrl || PLACEHOLDER_EVENT_IMAGE,
                    venue: event.venue,
                    date: format(new Date(event.startTime), 'do MMM, yyyy'),
                    time: new Date(event.startTime).toLocaleTimeString('en-US', { 
                        timeZone: DEFAULT_TIMEZONE,
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    }).replace(/am/g, 'AM').replace(/pm/g, 'PM'),
                    price: event.sellingPrice,
                    isFreeOfCharge: event.isFreeOfCharge,
                }));
                setRecommendedEvents(mappedRecommendedEvents);
            } catch (err: any) {
                setError(err.message || 'Failed to fetch events.');
            } finally {
                setLoading(false);
            }
        };

        loadEvents();
    }, []);



    return (
        <Box width="100vw" overflow="hidden">
            <AppMenu 
                onLoginClick={() => setIsLoginOpen(true)}
                onSignupClick={() => setIsSignupOpen(true)}
                currentPath={location.pathname}
            />
            <HeroCarousel images={HERO_BANNER_IMAGES} />

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            ) : (
                <>
                    <EventsSection title="Upcoming Events" events={nearbyEvents} seeAllLink="/upcoming-events" />
                    <CreateEventCTA />
                    <EventsSection title="Popular Events" events={popularEvents} seeAllLink="/popular-events" disabled={true} />
                    <EventsSection title="Recommended Events" events={recommendedEvents} seeAllLink="/recommended-events" disabled={true} />
                </>
            )}

            {/* Authentication Modals */}
            <LoginModal 
                open={isLoginOpen} 
                onClose={() => setIsLoginOpen(false)} 
                onSwitchToSignup={handleSwitchToSignup}
            />
            <SignupModal 
                open={isSignupOpen} 
                onClose={() => setIsSignupOpen(false)} 
                onSwitchToLogin={handleSwitchToLogin}
            />

        </Box>
    );
};

export default HomePage;
